/* eslint-disable react-hooks/exhaustive-deps */
import * as _ from "lodash";
import { createContext, FC, useContext, useEffect, useState } from "react";

import { isProduction } from "@ctra/utils";
import { UserEntity } from "@ctra/api";

import { useCurrentUser } from "@auth";

import { useLocalization } from "../LocalizationContext";

type IntercomType = (
  action: string,
  options?: string | Record<string, unknown> | (() => void),
  meta?: Record<string, unknown>
) => void;

declare global {
  interface Window {
    Intercom: IntercomType;
  }
}

/**
 * @type {string}
 */
export const intercomLauncherClassName = "ctra-subscriptions-intercom";

interface ContextType {
  /**
   * Intercom entity
   */
  entity: IntercomType;
  /**
   * Chat open or not
   */
  isChatOpen: boolean;
  /**
   * Count of unread messages
   */
  unreadCount: number;
}

/**
 * Make a default context for intercom state
 */
const DefaultContext = createContext<ContextType>({
  entity: () => void 0,
  isChatOpen: false,
  unreadCount: 0
});

interface ProviderProps extends Partial<UserEntity> {
  /**
   * default launcher visibilty
   */
  hideLauncher?: boolean;
}

/**
 * Intercom setup
 * @param username
 * @param hideLauncher
 */
export const _IntercomProvider: FC<ProviderProps> = ({ hideLauncher = true, children, ...props }) => {
  const { Intercom } = window;
  const [isChatOpen, setIsChatOpen] = useState<boolean>(false);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const { locale } = useLocalization();

  const {
    user: { userGroups }
  } = useCurrentUser();

  const { username } = props;

  /**
   * Launch event handler and cleanup
   */
  useEffect(() => {
    Intercom("boot", {
      app_id: isProduction() ? "pysg6fpw" : "bqmw0vq4",
      user_id: username,
      hide_default_launcher: hideLauncher,
      language_override: _.first(locale.split("-")),
      custom_launcher_selector: `.${intercomLauncherClassName}`,
      user_group: userGroups && userGroups.length ? _.map(userGroups, "name").join(", ") : null,
      user_type: "Enterprise"
    });

    return function cleanup() {
      Intercom("shutdown");
    };
  }, [Intercom, hideLauncher, username]);

  /**
   * Other interaction based event handlers
   */
  useEffect(() => {
    Intercom("onShow", () => {
      setIsChatOpen(true);
    });

    Intercom("onHide", () => {
      setIsChatOpen(false);
    });

    // @ts-ignore - intercom issue, does not recognise the count
    Intercom("onUnreadCountChange", (count) => {
      setUnreadCount(count);
    });
  }, [Intercom]);

  /**
   * Update language
   */
  useEffect(() => {
    Intercom("update", {
      language_override: _.first(locale.split("-"))
    });
  }, [locale]);

  return (
    <DefaultContext.Provider
      value={{
        entity: Intercom,
        isChatOpen,
        unreadCount
      }}
    >
      {children}
    </DefaultContext.Provider>
  );
};

export const IntercomContext = {
  Provider: _IntercomProvider,
  Consumer: DefaultContext.Consumer
};

/**
 * Make a hook to access the context in FCs
 */
export const useIntercom = (): ContextType => useContext(DefaultContext);
